import { useTheme } from "next-themes";
import { Moon, Sun } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

/**
 * ThemeToggle — cycles light ↔ dark using next-themes.
 *
 * We avoid rendering the icon on the server / during the first client
 * paint to prevent hydration mismatch; next-themes resolves the theme
 * after mount. `mounted` gates that.
 */
export function ThemeToggle({ className }: { className?: string }) {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const isDark = mounted && resolvedTheme === "dark";
  const next = isDark ? "light" : "dark";

  return (
    <button
      type="button"
      onClick={() => setTheme(next)}
      aria-label={`Switch to ${next} theme`}
      title={`Switch to ${next} theme`}
      className={cn(
        "flex h-8 w-8 items-center justify-center rounded-md text-ink-2 transition-colors duration-[120ms] hover:bg-canvas-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        className,
      )}
    >
      {/* Both icons rendered to avoid layout shift; only one is visible at a time. */}
      {mounted ? (
        isDark ? <Sun className="h-[15px] w-[15px]" aria-hidden="true" /> : <Moon className="h-[15px] w-[15px]" aria-hidden="true" />
      ) : (
        <span className="h-[15px] w-[15px]" aria-hidden="true" />
      )}
    </button>
  );
}
